import { useState, useEffect, useRef, useCallback } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import clsx from 'clsx'
import {
  forecastApi,
  ForecastResult,
  ForecastAnalysis,
} from '../utils/api'
import { notify } from '../stores/notificationStore'
import { useBrowserNotifications, useNotificationPreference } from '../hooks/useBrowserNotifications'
import {
  ACTIVE_FORECAST_KEY,
  HISTORY_REFETCH_ACTIVE_MS,
  HISTORY_REFETCH_IDLE_MS,
} from './forecast/constants'
import { ForecastInput } from './forecast/ForecastInput'
import { ForecastSidebar } from './forecast/ForecastSidebar'
import { AnalyzingStep } from './forecast/AnalyzingStep'
import { ConfirmationStep } from './forecast/ConfirmationStep'
import { ActiveForecast } from './forecast/ActiveForecast'
import { ForecastResultView } from './forecast/ForecastResultView'
import { EmptyState } from './forecast/EmptyState'
import { ConfirmModal } from './forecast/ConfirmModal'

export function Forecast() {
  // Core state
  const [question, setQuestion] = useState('')
  const [activeForecastId, setActiveForecastId] = useState<string | null>(null)
  const [activeResult, setActiveResult] = useState<{ forecastId: string; result: ForecastResult } | null>(null)
  const [selectedForecast, setSelectedForecast] = useState<string | null>(null)
  const [pendingAnalysis, setPendingAnalysis] = useState<ForecastAnalysis | null>(null)
  const [analyzingStages, setAnalyzingStages] = useState<
    Array<{ stage: string; message: string; data?: any }>
  >([])
  const [currentAnalyzeStage, setCurrentAnalyzeStage] = useState<string>('')

  // Mobile sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true)
  const [headerCollapsed, setHeaderCollapsed] = useState(false)

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)

  // Refs
  const inputRef = useRef<HTMLInputElement>(null)
  const analyzeCleanupRef = useRef<(() => void) | null>(null)
  const lastScrollY = useRef(0)

  // Router
  const [searchParams, setSearchParams] = useSearchParams()

  // Browser notifications
  const { sendNotification, requestPermission, permission, isSupported } = useBrowserNotifications()
  const { enabled: notificationsEnabled, setEnabled: setNotificationsEnabled } = useNotificationPreference()

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      analyzeCleanupRef.current?.()
    }
  }, [])

  // Handle scroll for header collapse on mobile
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const shouldCollapse = currentScrollY > 100 && currentScrollY > lastScrollY.current
      setHeaderCollapsed(shouldCollapse)
      lastScrollY.current = currentScrollY
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Deep-linking: /forecast?id=abcd1234
  useEffect(() => {
    const id = searchParams.get('id')
    if (id && id !== selectedForecast) {
      setSelectedForecast(id)
      setActiveResult(null)
    }
    if (!id && selectedForecast) {
      setSelectedForecast(null)
      setActiveResult(null)
    }
  }, [searchParams, selectedForecast])

  // Restore last active forecast (nice UX for refreshes)
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(ACTIVE_FORECAST_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw) as { forecastId?: string }
      if (parsed?.forecastId && !activeForecastId && !pendingAnalysis && !currentAnalyzeStage) {
        setActiveForecastId(parsed.forecastId)
        setSelectedForecast(parsed.forecastId)
        const next = new URLSearchParams(searchParams)
        next.set('id', parsed.forecastId)
        setSearchParams(next, { replace: true })
      }
    } catch {
      // ignore
    }
  }, [])

  // Fetch history
  const { data: historyData, refetch: refetchHistory } = useQuery({
    queryKey: ['forecast', 'history'],
    queryFn: () => forecastApi.history(50),
    refetchInterval: activeForecastId ? HISTORY_REFETCH_ACTIVE_MS : HISTORY_REFETCH_IDLE_MS,
  })

  // Start forecast mutation
  const startMutation = useMutation({
    mutationFn: (payload: {
      question: string
      category?: string
      target_date?: string
      topics?: string[]
      primary_entity?: string
      clarification?: string
    }) => forecastApi.start(payload.question, payload),
    onSuccess: (data) => {
      setActiveForecastId(data.forecast_id)
      setActiveResult(null)
      setSelectedForecast(data.forecast_id)
      setPendingAnalysis(null)
      setQuestion('')
      try {
        window.localStorage.setItem(ACTIVE_FORECAST_KEY, JSON.stringify({ forecastId: data.forecast_id }))
      } catch {
        // ignore
      }
      try {
        const next = new URLSearchParams(searchParams)
        next.set('id', data.forecast_id)
        setSearchParams(next, { replace: false })
      } catch {
        // ignore
      }
      refetchHistory()
      // Collapse sidebar on mobile when starting
      setSidebarCollapsed(true)
    },
  })

  // Analyze question mutation (fallback)
  const analyzeMutation = useMutation({
    mutationFn: (q: string) => forecastApi.analyze(q),
    onSuccess: (data) => {
      setPendingAnalysis(data)
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (forecastId: string) => forecastApi.delete(forecastId),
    onSuccess: (_data, forecastId) => {
      refetchHistory()
      setSelectedForecast((prev) => (prev === forecastId ? null : prev))
      setActiveResult((prev) => (prev?.forecastId === forecastId ? null : prev))
      setActiveForecastId((prev) => (prev === forecastId ? null : prev))
      try {
        const next = new URLSearchParams(searchParams)
        if (next.get('id') === forecastId) next.delete('id')
        setSearchParams(next, { replace: false })
      } catch {
        // ignore
      }
      notify.success('Forecast deleted')
    },
  })

  // Delete handlers
  const handleDeleteRequest = useCallback((forecastId: string) => {
    setDeleteTargetId(forecastId)
    setDeleteModalOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(() => {
    if (deleteTargetId) {
      deleteMutation.mutate(deleteTargetId)
    }
    setDeleteModalOpen(false)
    setDeleteTargetId(null)
  }, [deleteTargetId, deleteMutation])

  const handleDeleteCancel = useCallback(() => {
    setDeleteModalOpen(false)
    setDeleteTargetId(null)
  }, [])

  // Reuse question
  const handleReuseQuestion = useCallback((q: string) => {
    if (!q) return
    if (activeForecastId) return
    setQuestion(q)
    setPendingAnalysis(null)
    setCurrentAnalyzeStage('')
    setAnalyzingStages([])
    window.setTimeout(() => {
      inputRef.current?.focus()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }, 50)
  }, [activeForecastId])

  // Start streaming analysis
  const startAnalysis = useCallback(() => {
    if (!question.trim()) return

    analyzeCleanupRef.current?.()
    setAnalyzingStages([])
    setCurrentAnalyzeStage('parsing')

    analyzeCleanupRef.current = forecastApi.analyzeStream(
      question.trim(),
      (stage, data) => {
        setCurrentAnalyzeStage(stage)
        setAnalyzingStages((prev) => {
          if (prev.some((s) => s.stage === stage)) return prev
          return [...prev, { stage, message: data.message, data }]
        })
      },
      (analysis) => {
        setCurrentAnalyzeStage('')
        setAnalyzingStages([])
        setPendingAnalysis(analysis)
        analyzeCleanupRef.current = null
      },
      (error) => {
        setCurrentAnalyzeStage('')
        setAnalyzingStages([])
        console.error('Analysis error:', error)
        analyzeMutation.mutate(question.trim())
        analyzeCleanupRef.current = null
      }
    )
  }, [question, analyzeMutation])

  // Get selected forecast details
  const { data: selectedData } = useQuery({
    queryKey: ['forecast', 'detail', selectedForecast],
    queryFn: () => forecastApi.detail(selectedForecast!),
    enabled: !!selectedForecast && selectedForecast !== activeForecastId,
  })

  // Form handlers
  const handleSubmit = useCallback(() => {
    if (!question.trim() || currentAnalyzeStage || startMutation.isPending || activeForecastId || pendingAnalysis) {
      return
    }
    startAnalysis()
  }, [question, currentAnalyzeStage, startMutation.isPending, activeForecastId, pendingAnalysis, startAnalysis])

  const handleConfirmForecast = useCallback((overrides: {
    category?: string
    target_date?: string
    topics?: string[]
    primary_entity?: string
    clarification?: string
  }) => {
    if (!pendingAnalysis) return
    startMutation.mutate({ question: pendingAnalysis.raw_question, ...overrides })
  }, [pendingAnalysis, startMutation])

  const handleCancelConfirmation = useCallback(() => {
    setPendingAnalysis(null)
  }, [])

  const handleComplete = useCallback((forecastId: string, result: ForecastResult) => {
    setActiveResult({ forecastId, result })
    setActiveForecastId(null)
    setSelectedForecast(forecastId)
    try {
      window.localStorage.removeItem(ACTIVE_FORECAST_KEY)
    } catch {
      // ignore
    }
    try {
      const next = new URLSearchParams(searchParams)
      next.set('id', forecastId)
      setSearchParams(next, { replace: false })
    } catch {
      // ignore
    }
    refetchHistory()

    // Send browser notification if enabled
    if (notificationsEnabled && permission === 'granted') {
      const probText = result.report.probability !== null
        ? `${(result.report.probability * 100).toFixed(0)}%`
        : ''
      sendNotification(
        `Forecast Complete: ${probText}`,
        {
          body: result.report.question.slice(0, 100),
          tag: forecastId,
        }
      )
    }

    // Also show in-app notification
    notify.success(
      'Forecast Complete',
      `${(result.report.probability * 100).toFixed(0)}% probability`,
      { duration: 5000 }
    )
  }, [searchParams, setSearchParams, refetchHistory, notificationsEnabled, permission, sendNotification])

  const handleSelectForecast = useCallback((forecastId: string) => {
    if (activeForecastId && forecastId !== activeForecastId) return

    const nextSelected = forecastId === selectedForecast ? null : forecastId
    setSelectedForecast(nextSelected)
    setActiveResult(null)

    try {
      const next = new URLSearchParams(searchParams)
      if (nextSelected) next.set('id', nextSelected)
      else next.delete('id')
      setSearchParams(next, { replace: false })
    } catch {
      // ignore
    }

    if (!activeForecastId && nextSelected) {
      const meta = (historyData?.forecasts || []).find((f) => f.forecast_id === nextSelected)
      if (meta && (meta.status === 'running' || meta.status === 'analyzing' || meta.status === 'pending')) {
        setActiveForecastId(nextSelected)
        try {
          window.localStorage.setItem(ACTIVE_FORECAST_KEY, JSON.stringify({ forecastId: nextSelected }))
        } catch {
          // ignore
        }
      }
    }

    // Collapse sidebar on mobile when selecting
    setSidebarCollapsed(true)
  }, [activeForecastId, selectedForecast, searchParams, setSearchParams, historyData])

  // Handle example question click
  const handleExampleClick = useCallback((exampleQuestion: string) => {
    setQuestion(exampleQuestion)
    inputRef.current?.focus()
  }, [])

  // Enable notifications
  const handleEnableNotifications = useCallback(async () => {
    const granted = await requestPermission()
    if (granted) {
      setNotificationsEnabled(true)
      notify.success('Notifications enabled', 'You\'ll be notified when forecasts complete')
    }
  }, [requestPermission, setNotificationsEnabled])

  // Data
  const forecasts = historyData?.forecasts || []
  const totalForecasts = historyData?.total || 0
  const completedForecasts = forecasts.filter((f) => f.status === 'completed' || f.status === 'failed')
  const avgProbability =
    completedForecasts.length > 0
      ? completedForecasts
          .filter((f) => f.probability !== null)
          .reduce((sum, f) => sum + (f.probability || 0), 0) /
        completedForecasts.filter((f) => f.probability !== null).length
      : null

  const isDisabled = !!currentAnalyzeStage || startMutation.isPending || !!activeForecastId || !!pendingAnalysis

  return (
    <div className="min-h-screen bg-void">
      {/* Header */}
      <div
        className={clsx(
          'border-b border-star-dim/10 bg-nebula/50 backdrop-blur-sm sticky top-0 z-10 transition-transform duration-300',
          headerCollapsed && 'lg:translate-y-0 -translate-y-full'
        )}
      >
        <div className="max-w-6xl mx-auto px-4 py-4 lg:py-6">
          <div className="flex items-center gap-4 mb-4 lg:mb-6">
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-gradient-to-br from-signal-blue/20 to-signal-purple/20 flex items-center justify-center border border-signal-blue/30">
              <span className="text-xl lg:text-2xl">🔮</span>
            </div>
            <div className="flex-1">
              <h1 className="text-xl lg:text-2xl font-display font-bold text-star">Forecast</h1>
              <p className="text-xs lg:text-sm text-star-muted hidden sm:block">AI-powered prediction engine</p>
            </div>

            {/* Notification toggle */}
            {isSupported && (
              <div className="hidden sm:block">
                {permission === 'granted' ? (
                  <button
                    onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                    className={clsx(
                      'px-3 py-1.5 rounded-lg text-xs font-mono border transition-all',
                      notificationsEnabled
                        ? 'bg-signal-blue/10 border-signal-blue/30 text-signal-blue'
                        : 'bg-void-50 border-star-dim/20 text-star-dim hover:text-star'
                    )}
                  >
                    🔔 {notificationsEnabled ? 'On' : 'Off'}
                  </button>
                ) : permission !== 'denied' && (
                  <button
                    onClick={handleEnableNotifications}
                    className="px-3 py-1.5 rounded-lg text-xs font-mono bg-void-50 border border-star-dim/20 text-star-dim hover:text-star transition-all"
                  >
                    🔔 Enable notifications
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Input */}
          <ForecastInput
            value={question}
            onChange={setQuestion}
            onSubmit={handleSubmit}
            isDisabled={isDisabled}
            isAnalyzing={!!currentAnalyzeStage}
            isRunning={startMutation.isPending || !!activeForecastId}
          />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 lg:py-6 overflow-hidden">
        <div className="grid lg:grid-cols-3 gap-4 lg:gap-6 overflow-hidden">
          {/* Left: History - Hidden on mobile by default */}
          <div
            className={clsx(
              'lg:col-span-1',
              'fixed inset-0 z-40 lg:relative lg:inset-auto',
              'bg-void lg:bg-transparent',
              'p-4 lg:p-0',
              'overflow-auto',
              'transition-transform duration-300 lg:transform-none',
              sidebarCollapsed ? 'translate-x-full lg:translate-x-0' : 'translate-x-0'
            )}
          >
            <ForecastSidebar
              forecasts={forecasts}
              totalForecasts={totalForecasts}
              avgProbability={avgProbability}
              activeForecastId={activeForecastId}
              selectedForecast={selectedForecast}
              onSelectForecast={handleSelectForecast}
              onDeleteForecast={handleDeleteRequest}
              isCollapsed={sidebarCollapsed}
              onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            />
          </div>

          {/* Mobile sidebar toggle FAB */}
          {sidebarCollapsed && (
            <button
              onClick={() => setSidebarCollapsed(false)}
              className="lg:hidden fixed bottom-4 left-4 z-50 glass-card p-3 rounded-full shadow-lg flex items-center gap-2"
            >
              <span className="text-lg">📋</span>
              <span className="text-xs font-mono text-star-muted">{totalForecasts}</span>
            </button>
          )}

          {/* Right: Active/Selected forecast */}
          <div className="lg:col-span-2 min-w-0">
            <div className="glass-card p-3 sm:p-4 lg:p-6 min-h-[400px] overflow-hidden">
              {currentAnalyzeStage ? (
                <AnalyzingStep stages={analyzingStages} currentStage={currentAnalyzeStage} />
              ) : pendingAnalysis ? (
                <ConfirmationStep
                  analysis={pendingAnalysis}
                  onConfirm={handleConfirmForecast}
                  onCancel={handleCancelConfirmation}
                  isSubmitting={startMutation.isPending}
                />
              ) : activeForecastId ? (
                <ActiveForecast
                  forecastId={activeForecastId}
                  onComplete={(result) => handleComplete(activeForecastId!, result)}
                />
              ) : activeResult ? (
                <ForecastResultView
                  forecastId={activeResult.forecastId}
                  result={activeResult.result}
                  onReuseQuestion={handleReuseQuestion}
                  onDelete={() => handleDeleteRequest(activeResult.forecastId)}
                />
              ) : selectedForecast && selectedData && (selectedData.status === 'running' || selectedData.status === 'analyzing' || selectedData.status === 'pending') ? (
                <ActiveForecast
                  forecastId={selectedForecast}
                  onComplete={(result) => handleComplete(selectedForecast!, result)}
                />
              ) : selectedForecast && selectedData?.result ? (
                <ForecastResultView
                  forecastId={selectedForecast}
                  result={selectedData.result}
                  onReuseQuestion={handleReuseQuestion}
                  onDelete={() => handleDeleteRequest(selectedForecast)}
                />
              ) : selectedForecast && selectedData?.status === 'failed' ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <span className="text-4xl mb-4">❌</span>
                  <h3 className="text-lg text-star mb-2">Forecast Failed</h3>
                  <p className="text-star-muted text-sm max-w-md">
                    {selectedData.error || 'An error occurred while processing this forecast.'}
                  </p>
                  <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleReuseQuestion(selectedData.question)}
                      className="px-4 py-2 rounded-lg text-sm font-mono bg-void-50 border border-star-dim/20 text-star-muted hover:text-star transition-all"
                    >
                      Reuse question
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteRequest(selectedForecast)}
                      className="px-4 py-2 rounded-lg text-sm font-mono bg-signal-red/10 border border-signal-red/30 text-signal-red hover:bg-signal-red/15 transition-all"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ) : (
                <EmptyState onSelectExample={handleExampleClick} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        title="Delete Forecast"
        message="Are you sure you want to delete this forecast? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  )
}
